/**
 * Levels, as a video game's: what the athlete has banked on each drill across
 * every visit, not just this session.
 *
 * The honest version of a level. XP is **clean reps** — reps with no failed
 * criterion — and nothing else, so the number cannot be farmed by doing more
 * bad reps, which is the one thing a rep counter makes trivially easy and the
 * one thing a coaching app must not reward. A rep that was only partly seen
 * earns nothing either: it was not judged, so it is not evidence of anything.
 *
 * It is a motivator and not a measurement. A level says how much clean work has
 * gone in, not how good the athlete is, and on an uncalibrated drill the clean
 * reps were counted against thresholds nobody measured — the menu card and the
 * receipt both keep saying so, and the level does not override that.
 *
 * Stored in localStorage, per device. There is no backend and no account in this
 * app, which is the whole premise: no video and nothing derived from it leaves
 * the machine. So the levels live on the phone that recorded them, and clearing
 * the browser's data clears them. Every access is wrapped: Safari in private
 * mode throws on write rather than returning null, and a drill that cannot save
 * a level must still run.
 */

const KEY = "spottr.progress.v1";
/**
 * What the key was called before the app was renamed from RoboCoach.
 *
 * A rename must not cost the athlete their levels. The keys are read through
 * `readRenamed`, which copies the old record forward the first time it finds
 * one, so a phone that has been using the app keeps every clean rep it earned
 * and a new one never writes the old name at all.
 */
const OLD_KEY = "robocoach.progress.v1";

/**
 * Clean reps for the first level, and how much more each level asks.
 *
 * The curve is deliberately gentle at the start and never steepens much: the
 * point is that a first session ends with something visibly earned, and that a
 * drill done twice a week keeps moving. Ten clean reps is one honest set.
 */
const BASE = 10;
const STEP = 5;

/** Clean reps needed to go from `level` to the next one. */
export function costOf(level) {
  return BASE + STEP * (level - 1);
}

/**
 * Level, and progress through it, for a total of clean reps.
 *
 * @return `{ level, into, need }` — the level reached, clean reps banked into
 *   it, and what the next one costs.
 */
export function levelFor(clean) {
  let level = 1;
  // A total read back from storage can be anything; a drill with a corrupt
  // total is level one, not a bar full of NaN.
  let left = Number.isFinite(clean) ? Math.max(0, Math.floor(clean)) : 0;
  while (left >= costOf(level)) {
    left -= costOf(level);
    level += 1;
  }
  return { level, into: left, need: costOf(level) };
}

export class Progress {
  constructor(storage = safeStorage()) {
    this.storage = storage;
    /** Per exercise id: `{ reps, clean }`. */
    this.totals = this.read();
  }

  read() {
    if (this.storage == null) {
      return {};
    }
    try {
      const raw = readRenamed(this.storage, KEY, OLD_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      // Anything in storage is from an older build or a hand edit, so each
      // field is taken only if it is a real number.
      const totals = {};
      for (const [id, entry] of Object.entries(parsed || {})) {
        if (entry && typeof entry === "object") {
          totals[id] = {
            reps: numberOr(entry.reps, 0),
            clean: numberOr(entry.clean, 0),
          };
        }
      }
      return totals;
    } catch {
      return {};
    }
  }

  save() {
    if (this.storage == null) {
      return;
    }
    try {
      this.storage.setItem(KEY, JSON.stringify(this.totals));
    } catch {
      // Out of quota, or private mode. A level that cannot be saved is not a
      // reason to lose the set that earned it.
    }
  }

  forExercise(id) {
    return this.totals[id] || { reps: 0, clean: 0 };
  }

  /** `{ level, into, need }` for one drill. */
  levelOf(id) {
    return levelFor(this.forExercise(id).clean);
  }

  /**
   * Banks a finished set.
   *
   * @return `{ gained, from, to }` — clean reps earned and the levels either
   *   side, so the receipt can say when one was crossed.
   */
  add(set) {
    const id = set.exercise && set.exercise.id;
    if (id == null || set.reps === 0) {
      return { gained: 0, from: null, to: null };
    }
    const before = this.levelOf(id);
    const totals = this.forExercise(id);
    this.totals[id] = {
      reps: totals.reps + set.reps,
      clean: totals.clean + set.good,
    };
    this.save();
    return { gained: set.good, from: before.level, to: this.levelOf(id).level };
  }

  /** Wipes every level. Only ever called from an explicit reset. */
  clear() {
    this.totals = {};
    this.save();
  }
}

function numberOr(value, fallback) {
  return Number.isFinite(value) && value >= 0 ? value : fallback;
}

/**
 * localStorage where it works, null where it does not. Touching the property at
 * all throws in some privacy modes, so even reading it is guarded.
 *
 * Exported because the day's receipt is kept the same way; see
 * metrics/history.js.
 */
export function safeStorage() {
  try {
    const storage = window.localStorage;
    const probe = "spottr.probe";
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

/**
 * Reads a key, falling back to the name it had before the app was renamed, and
 * copies what it finds forward.
 *
 * The app was RoboCoach and is now Spottr, and the levels and the day's sets
 * both live in `localStorage` on the one phone that earned them — there is no
 * account and no backend to restore them from, so a renamed key is the same as
 * a wiped one. This runs once per phone: after the copy the old record is left
 * in place rather than deleted, because a build rolled back should still find
 * it, and it costs a few hundred bytes.
 *
 * Exported because the day's receipt is kept the same way; see
 * metrics/history.js.
 */
export function readRenamed(storage, key, oldKey) {
  if (storage == null) {
    return null;
  }
  try {
    const raw = storage.getItem(key);
    if (raw != null) {
      return raw;
    }
    const legacy = storage.getItem(oldKey);
    if (legacy == null) {
      return null;
    }
    try {
      storage.setItem(key, legacy);
    } catch {
      // Out of quota or private mode. The record still reads fine from the old
      // key this time and every time, so nothing is lost by failing here.
    }
    return legacy;
  } catch {
    return null;
  }
}
